import { ApplicationLog, LogFilter, LogResponse, LogStatistics } from '../types/ApplicationLog';
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class LogApi {
  async getLogs(filter: LogFilter): Promise<LogResponse> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs?${queryParams}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching logs:', error);
      throw error;
    }
  }

  async getLogById(id: number): Promise<ApplicationLog> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs/${id}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch log');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching log:', error);
      throw error;
    }
  }

  async getLevels(): Promise<string[]> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs/levels`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch levels');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching log levels:', error);
      throw error;
    }
  }

  async clearLogs(beforeDate?: string): Promise<{ deletedCount: number }> {
    try {
      const queryParams = beforeDate ? `?beforeDate=${beforeDate}` : '';
      const response = await AuthService.authenticatedDelete(
        `${API_BASE_URL}/applicationlogs/clear${queryParams}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to clear logs');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error clearing logs:', error);
      throw error;
    }
  }

  async getRequestTrace(correlationId: string): Promise<ApplicationLog[]> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs/trace/${correlationId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch request trace');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching request trace:', error);
      throw error;
    }
  }

  async searchLogs(searchTerm: string, pageSize: number = 100, pageNumber: number = 1): Promise<LogResponse> {
    try {
      const queryParams = new URLSearchParams({
        searchTerm,
        pageSize: pageSize.toString(),
        pageNumber: pageNumber.toString(),
      });
      
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs/search?${queryParams}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to search logs');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching logs:', error);
      throw error;
    }
  }

  async getStatistics(fromDate?: string, toDate?: string): Promise<LogStatistics> {
    try {
      const queryParams = new URLSearchParams();
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);
      
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs/statistics?${queryParams}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  async exportToCsv(level?: string, fromDate?: string, toDate?: string): Promise<Blob> {
    try {
      const queryParams = new URLSearchParams();
      if (level) queryParams.append('level', level);
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);
      
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs/export/csv?${queryParams}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to export to CSV');
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      throw error;
    }
  }

  async exportToExcel(level?: string, fromDate?: string, toDate?: string): Promise<Blob> {
    try {
      const queryParams = new URLSearchParams();
      if (level) queryParams.append('level', level);
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);
      
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/applicationlogs/export/excel?${queryParams}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to export to Excel');
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw error;
    }
  }

  downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new LogApi();